<template>
  <transition appear name="fade">
    <div class="container-fluid px-0 mb-5" v-if="isMobileOrTablet">
      <NoProductsPopup v-if="!hasProduct && !spin" />
      <div class="row justify-content-center" style="z-index: 0" v-if="!spin">
        <b-button-group class="text-center">
          <!-- ... (your existing code) -->
        </b-button-group>
      </div>
      <div class="mobile-product-list">
        <div id="spinner" v-show="spin" class="spin-clss">
          <b-spinner :key="spin" label="Spinning" variant="primary"></b-spinner>
        </div>
        <div class="mobile-grid">
          <div v-for="product in products" :key="product.id" class="mobile-grid-item">
            <router-link
              :to="{
                name: 'Product',
                params: { productId: product.id, productCategoryId: currentCategoryId },
              }"
              class="mobile-product-card"
            >
              <div class="mobile-product-card__image">
                <img :src="product.imageUrl" />
              </div>
              <div class="mobile-product-card__body">
                <p class="mobile-product-card__name">{{ product.name }}</p>
                <div class="mobile-product-card__price">
                  <span class="mobile-product-card__amount">{{ product.points | numToLocale }}</span>
                  <span class="mobile-product-card__pts">pts</span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
      <div class="row justify-content-center mt-3" style="z-index: 0" v-if="!spin">
        <b-button-group class="text-center mobile-pagination">
          <b-button :disabled="!hasPrevious" v-on:click="previousPage">‹ Prev</b-button>
          <b-button class="current-page">{{ currentPage + 1 }}</b-button>
          <b-button :disabled="!hasNext" v-on:click="nextPage">Next ›</b-button>
        </b-button-group>
      </div>
    </div>

    <!-- ===== DESKTOP ===== -->
    <div class="container-fluid mb-5 desktop-grid-wrapper" v-else>
      <NoProductsPopup v-if="!hasProduct && !spin" />

      <div id="spinner" v-show="spin" class="spin-clss">
        <b-spinner :key="spin" label="Spinning" variant="primary"></b-spinner>
      </div>

      <div class="row product-grid px-1" v-show="!spin">
        <div
          v-for="(product, index) in products"
          :key="product.id"
          class="product col-lg-3 col-md-4 col-sm-6 col-xs-12 mb-3"
        >
          <router-link
            :to="{
              name: 'Product',
              params: { productId: product.id, productCategoryId: currentCategoryId },
            }"
            class="product-card"
          >
            <div class="image">
              <img :src="product.imageUrl" />
              <div class="product-overlay">
                <span class="view-label">View Product</span>
              </div>
            </div>
            <div class="product-body">
              <p class="name">{{ product.name }}</p>
              <div class="price-badge">
                <span class="price-badge__amount">{{ product.points | numToLocale }}</span>
                <span class="price-badge__label">pts</span>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <div class="row justify-content-center mt-2" style="z-index: 0">
        <b-button-group class="text-center desktop-pagination">
          <b-button :disabled="!hasPrevious" @click="previousPage">‹ Prev</b-button>
          <b-button
            v-for="pageNumber in visiblePageNumbers"
            :key="pageNumber"
            :class="{ 'active-page': currentPage === pageNumber }"
            :disabled="currentPage === pageNumber"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber + 1 }}
          </b-button>
          <b-button :disabled="!hasNext" @click="nextPage">Next ›</b-button>
        </b-button-group>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from "vuex";
import { isMobile, isTablet } from "mobile-device-detect";
import NoProductsPopup from "@/components/NoProductsPopup.vue";

export default {
  components: { NoProductsPopup },
  props: {
    searchFilter: String,
    sortOrder: String,
    pointFilter: String,
    limitFilter: String,
  },
  watch: {
    currentCategoryId: function (newValue, oldValue) {
      this.currentPage = 0;
      this.fetchProducts(newValue, this.searchFilter, this.sortOrder, this.currentPage, this.pointFilter, this.limitFilter);
    },
    searchFilter: function (newValue, oldValue) {
      this.currentPage = 0;
      this.fetchProducts(this.currentCategoryId, newValue, this.sortOrder, this.currentPage, this.pointFilter, this.limitFilter);
    },
    sortOrder: function (newValue, oldValue) {
      console.log('[watch] sortOrder', oldValue, '->', newValue);
      this.currentPage = 0;
      this.fetchProducts(this.currentCategoryId, this.searchFilter, newValue, this.currentPage, this.pointFilter, this.limitFilter);
    },
    currentPage: function (newValue, oldValue) {
      this.fetchProducts(this.currentCategoryId, this.searchFilter, this.sortOrder, newValue, this.pointFilter, this.limitFilter);
    },
    pointFilter(newVal) {
      console.log('[watch] pointFilter', newVal);
      if (newVal && newVal.includes("undefined")) return; // only guard against malformed "undefined" values
      this.currentPage = 0;
      this.fetchProducts(this.currentCategoryId, this.searchFilter, this.sortOrder, this.currentPage, newVal, this.limitFilter);
    },
    limitFilter: function (newValue, oldValue) {
      console.log('[watch] limitFilter', oldValue, '->', newValue);
      this.fetchProducts(this.currentCategoryId, this.searchFilter, this.sortOrder, this.currentPage, this.pointFilter, newValue);
    },
  },
  data() {
    return {
      currentPage: 0,
      spin: false,
      userProfile: {},
    };
  },
  computed: {
    ...mapState({
      rootCategoryId: (state) => state.category.rootCategory.id,
      currentCategoryId: (state) => state.category.currentCategoryId,
      products: (state) => state.product.items,
      max_page: (state) => state.product.max_page,
    }),
    isMobileOrTablet: function () { return isMobile || isTablet; },
    hasPrevious: function () { return this.currentPage > 0; },
    hasProduct: function () { return this.products.length > 0; },
    hasNext() { return this.products.length === Number(this.limitFilter); },
    visiblePageNumbers: function () {
      const pageCount = this.max_page;
      const maxVisiblePages = 9;
      let startPage;
      if (this.currentPage < maxVisiblePages - 1) {
        startPage = 0;
      } else if (this.currentPage > pageCount - maxVisiblePages) {
        startPage = pageCount - maxVisiblePages;
      } else {
        startPage = this.currentPage - Math.floor(maxVisiblePages / 2);
      }
      if (startPage < 0) startPage = 0;
      return Array.from({ length: Math.min(maxVisiblePages, pageCount) }, (_, index) => startPage + index);
    },
  },
  methods: {
    async fetchProducts(categoryId, searchFilter, sortOrder, currentPage, pointFilter, limitFilter = 20) {
      this.spin = true;
      this.$store.commit("product/SET_ITEMS", {});
      let finalCategoryId = categoryId;
      if (categoryId === this.rootCategoryId) finalCategoryId = null;
      try {
        await this.$store.dispatch("product/fetchProducts", {
          categoryId: finalCategoryId, searchFilter, sortOrder,
          page: currentPage, pointFilter, limitFilter,
        });
      } finally {
        this.spin = false;
      }
    },
    nextPage() { if (this.hasNext) this.currentPage += 1; },
    previousPage() { if (this.hasPrevious) this.currentPage -= 1; },
    goToPage(pageNumber) { if (pageNumber !== this.currentPage) this.currentPage = pageNumber; },
  },
  async created() {
    await this.fetchProducts(this.currentCategoryId, this.searchFilter, this.sortOrder, this.currentPage);
    this.userProfile = this.$store.getters["user/getUserProfile"];
  },
};
</script>

<style lang="scss">
/* ─── SHARED ──────────────────────────────────────────── */
.fade-enter-active { transition: opacity 0.35s ease; }
.fade-enter       { opacity: 0; }

/* ─── PRODUCT CARD (DESKTOP) ──────────────────────────── */
.product-card {
  display: flex;
  flex-direction: column;
  background: #f4f5f9;
  border: 1px solid #eaecf4;
  border-radius: 14px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.25s, transform 0.25s;
  height: 100%;

  &:hover {
    box-shadow: 0 8px 28px rgba(0,0,0,0.12);
    transform: translateY(-3px);
    text-decoration: none;
    color: inherit;
  }

  .image {
    position: relative;
    overflow: hidden;
    background: #fff;
    height: 200px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 12px;
      transition: transform 0.35s ease;
    }
  }

  &:hover .image img { transform: scale(1.04); }

  .product-overlay {
    position: absolute;
    inset: 0;
    background: rgba(79,110,247,0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.25s;
    .view-label {
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
      border: 2px solid rgba(255,255,255,0.7);
      padding: 6px 18px;
      border-radius: 20px;
    }
  }

  &:hover .product-overlay { opacity: 1; }

  .product-body {
    padding: 12px 14px 14px;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 6px;
    background: #f4f5f9;
  }

  .name {
    font-size: 13.5px;
    font-weight: 500;
    color: #2d3142;
    margin: 0;
    line-height: 1.4;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

/* ─── PRODUCT CARD (MOBILE) 2-col grid ───────────────── */
.mobile-product-list {
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}

/* 2-column grid */
.mobile-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mobile-grid-item {
  min-width: 0;
}

.mobile-product-card {
  display: flex;
  flex-direction: column;
  background: #f4f5f9;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  border: 1px solid #eaecf4;
  transition: box-shadow 0.2s, transform 0.2s;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    transform: translateY(-2px);
    text-decoration: none;
    color: inherit;
  }

  &__image {
    width: 100%;
    aspect-ratio: 1 / 1;
    background: #fff;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 8px;
      transition: transform 0.3s ease;
    }
  }

  &:hover &__image img {
    transform: scale(1.04);
  }

  &__body {
    padding: 8px 10px 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
  }

  &__name {
    font-size: 12.5px;
    font-weight: 500;
    color: #2d3142;
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__price {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    margin-top: auto;
  }

  &__amount {
    font-size: 14px;
    font-weight: 800;
    color: #3782f3;
    letter-spacing: -0.3px;
    line-height: 1;
  }

  &__pts {
    font-size: 9px;
    font-weight: 700;
    color: #3782f3;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

/* ─── PRICE BADGE (desktop) ───────────────────────────── */
.price-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  margin-top: auto;
  padding: 5px 10px 5px 8px;
  /*background: linear-gradient(135deg, #eef1ff 0%, #e6ebff 100%);
  border: 1.5px solid #c7d0fa;*/
  border-radius: 8px;
  width: fit-content;

  &__amount {
    font-size: 15px;
    font-weight: 800;
    color: #3782f3;
    letter-spacing: -0.3px;
    line-height: 1;
  }

  &__label {
    font-size: 10px;
    font-weight: 700;
    color: #3782f3;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    line-height: 1;
  }

  /* Mobile variant — slightly smaller */
  &--mobile {
    padding: 4px 8px 4px 7px;
    border-radius: 6px;
    .price-badge__amount { font-size: 13px; }
    .price-badge__label  { font-size: 9px; }
  }
}

/* ─── SPINNER ─────────────────────────────────────────── */
.spin-clss {
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ─── PAGINATION ──────────────────────────────────────── */
.desktop-pagination,
.mobile-pagination {
  .btn {
    border: 1px solid #dde1eb !important;
    background: #fff !important;
    color: #4f6ef7 !important;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 14px;
    transition: background 0.2s, color 0.2s;

    &:hover:not(:disabled) {
      background: #4f6ef7 !important;
      color: #fff !important;
    }

    &:disabled { color: #ccc !important; cursor: default; }
  }

  .active-page,
  .current-page {
    background: #4f6ef7 !important;
    color: #fff !important;
    font-weight: 700;
  }
}

/* ─── RESPONSIVE ──────────────────────────────────────── */
@media (max-width: 360px) {
  .mobile-grid { gap: 7px; }
  .mobile-product-card__name { font-size: 11.5px; }
  .mobile-product-card__amount { font-size: 13px; }
}

@media (min-width: 992px) {
  .desktop-grid-wrapper { background: transparent; }
}
</style>